<script setup lang="ts">
import { computed, ref } from 'vue';
import type { GarageItem } from '@/net/api';
import ItemPreview from './ItemPreview.vue';
import StatList from './StatList.vue';
import TitleBadge from '@/ui/TitleBadge.vue';
import CurrencyIcon from '@/ui/CurrencyIcon.vue';
import UpgradeInstallButton from './UpgradeInstallButton.vue';
import { HULL_STATS, TURRET_STATS, formatCredits } from './stat-display';
import { liveryFor, STOCK_PAINT_ID } from '@shared/liveries';
import { titleFor } from '@shared/titles';

/**
 * One catalogue entry: what it is, what it looks like, what it costs, and what
 * fitting it would do to the tank you already have.
 *
 * Every button here is a request the server re-validates. Disabling a button
 * for an unaffordable item is a courtesy to the player, never the enforcement —
 * that lives in `GarageService`.
 */
const props = defineProps<{
  item: GarageItem;
  /** The equivalent part currently fitted, for the stat comparison. */
  current: GarageItem | null;
  /**
   * The *other* half of the loadout — the equipped turret on a hull card, the
   * equipped hull on a turret card.
   *
   * The preview draws the item on the tank the player actually has, so a hull
   * card answers "what would mine look like" rather than showing a chassis with
   * a stranger's gun on it.
   */
  counterpartId: string | null;
  /**
   * The player's own call sign, for the mock nameplate on a title card.
   *
   * Their name rather than a placeholder, because the card's job is to answer
   * *what will this look like over my tank* — and a title's whole product is
   * how it sits next to the name it is worn with. Falls back to a stand-in when
   * the screen has not got one yet.
   */
  nickname?: string;
  credits: number;
  diamonds: number;
  busy: boolean;
}>();

const emit = defineEmits<{
  purchase: [id: string];
  upgrade: [id: string];
  /** Install a finished upgrade. */
  installUpgrade: [id: string];
  /** Finish a running install now, for diamonds. */
  skipUpgrade: [id: string];
  prestige: [id: string];
  equip: [id: string];
  customise: [id: string];
  /**
   * "Take me to the node this card is waiting on."
   *
   * Emitted rather than acted on: switching tabs is `GarageScreen`'s to do, and
   * a card that reached into the screen's tab state would make every card in
   * the grid a second owner of it. The card's job ends at naming the node.
   */
  showResearch: [nodeId: string];
  /**
   * "Open this one at inspection size" — the workbench.
   *
   * Raised by a click anywhere on the card that was not aimed at one of its own
   * controls. The card stays a card: it does not know what a workbench is, and
   * the window is mounted by `GarageScreen` over the whole screen rather than
   * inside the grid's stacking context, exactly as the customise window and the
   * rank ladder are.
   */
  open: [id: string];
}>();

/**
 * A click on the card body opens the workbench; a click on a control does not.
 *
 * One handler on the article with a guard, rather than a wrapper element around
 * the parts that should be clickable. The card carries up to four buttons — buy,
 * fit, upgrade, customize, plus the gate link — and every one of them is a
 * *different* action from "show me more", so the guard is what keeps a mis-aimed
 * click from doing two things at once. `closest` rather than checking the target
 * itself, because the pressable thing is often a `<small>` or a `<template>`'s
 * text inside the button.
 *
 * Cosmetic cards are deliberately excluded upstream — a livery's product is its
 * texture and a title's is its badge, both of which the card already shows at
 * full size, so there is nothing a bigger window would add. See `openable`.
 */
function onCardClick(event: MouseEvent): void {
  if (!openable.value) return;
  const target = event.target as HTMLElement | null;
  if (target?.closest('button, a, input, select, textarea')) return;
  emit('open', props.item.id);
}

const descriptors = computed(() =>
  props.item.kind === 'HULL' ? HULL_STATS : props.item.kind === 'TURRET' ? TURRET_STATS : [],
);

const isPaint = computed(() => props.item.kind === 'PAINT');
/**
 * A title is the one card whose preview *is* the product.
 *
 * A hull card shows a still of a mesh; a title card shows the badge itself,
 * through the same `TitleBadge` the nameplate over the tank is drawn with. There
 * is no rendering path here that the arena does not also take, so the card
 * cannot flatter what the player will actually get.
 */
const isTitle = computed(() => props.item.kind === 'TITLE');

/**
 * How much faster a slow title animation runs in the shop than in battle.
 * Eclipse's transit is a whole 11-minute battle; at this speed a buyer sees it
 * in under a minute and a half. Labelled on the card, so nobody mistakes the
 * preview's pace for the real one.
 */
const TITLE_PREVIEW_SPEED = 8;

/** Only the eclipse is slow enough to need speeding up — and labelling. */
const spedUp = computed(() => isTitle.value && titleFor(props.item.id)?.animated === 'eclipse');
/**
 * Two gates, checked the same way and reported separately.
 *
 * `locked` is *owned* against either gate, never against the gates alone — a
 * gate is permission to buy and never permission to keep, so an item bought
 * before its gate existed carries no lock at all and fits exactly as it did.
 * The two halves are kept apart below because a card that says only "Locked"
 * answers none of the question a player actually has.
 */
const researchLocked = computed(() => !props.item.owned && !props.item.researchSatisfied);
const rankLocked = computed(() => !props.item.owned && !props.item.rankSatisfied);
const locked = computed(() => researchLocked.value || rankLocked.value);

/**
 * Which currency this card is priced in.
 *
 * A premium item keeps a credit `price`, and that figure is a **valuation the
 * shop never charges** — so printing it here would put a number in the largest
 * text on the card that nobody is ever asked for. The same rule `priceLabel`
 * exists for on the diamond packs: the headline is always what is paid.
 *
 * The gates are unchanged either way. A premium item is bought with a different
 * currency, not past a different rule — research and rank apply exactly as they
 * do to the credit ladder, which is what makes the premium shelf a ladder too.
 */
const isPremium = computed(() => props.item.diamondPrice !== null);
/**
 * Two ways an item can be on this screen without being for sale, and they are
 * separate states because they answer different questions.
 *
 * **`retired`** — cut from the catalogue and still owned. It only ever reaches a
 * card the player owns, so the question is *why is there no price on my paint*,
 * and the answer is that there is no longer one to quote.
 *
 * **`awardedAtRank`** — given at a rank rather than sold. This one arrives on the
 * card whether it is owned or not, because it is a thing to climb towards. It is
 * deliberately **not** rendered as a locked card: a lock says *pay once you get
 * there*, and there is no price at any rank. So the buy control is absent rather
 * than disabled — the honest shape of "nothing to buy here".
 */
const awardedAt = computed(() => props.item.awardedAtRank);
const forSale = computed(() => !props.item.retired && awardedAt.value === null);
/**
 * The price, split into a figure and a currency so the unit can be a drawn mark.
 *
 * It used to be one string — `` `${diamondPrice} 💎` `` or `` `${price} cr` `` —
 * and a baked-in unit cannot become a component. So the figure and the kind are
 * separate now, and the two non-numeric states keep a word: an item that is not
 * for sale has no figure for a mark to sit beside, and *No longer sold* is the
 * whole answer to why there is no price on the card.
 */
const priceAmount = computed(() =>
  props.item.retired || awardedAt.value !== null
    ? null
    : isPremium.value
      ? String(props.item.diamondPrice)
      : formatCredits(props.item.price),
);
const priceKind = computed<'credits' | 'diamonds'>(() =>
  isPremium.value ? 'diamonds' : 'credits',
);
/** What stands in when there is no figure. */
const priceWord = computed(() => (props.item.retired ? 'No longer sold' : 'Awarded'));
const canBuy = computed(
  () =>
    forSale.value &&
    !props.item.owned &&
    props.item.researchSatisfied &&
    props.item.rankSatisfied &&
    (isPremium.value
      ? props.diamonds >= (props.item.diamondPrice ?? 0)
      : props.credits >= props.item.price),
);
const canUpgrade = computed(
  () =>
    props.item.owned &&
    props.item.nextUpgradeCost !== null &&
    props.credits >= props.item.nextUpgradeCost,
);
const maxed = computed(
  () =>
    props.item.owned &&
    props.item.maxUpgradeLevel > 0 &&
    props.item.nextUpgradeCost === null &&
    props.item.upgrade === null,
);

/**
 * Top tier is offered only once the credit ladder is finished — it is levels
 * *beyond* what credits can buy, not a way around them. The server enforces
 * that; this only decides whether to draw the control.
 */
const showsPrestige = computed(() => props.item.owned && props.item.maxPrestigeLevel > 0);
const prestigeMaxed = computed(
  () => props.item.prestigeUnlocked && props.item.nextPrestigeCost === null,
);
const canPrestige = computed(
  () =>
    props.item.prestigeUnlocked &&
    props.item.nextPrestigeCost !== null &&
    props.diamonds >= props.item.nextPrestigeCost,
);

/** Compare against the fitted part, unless this *is* the fitted part. */
const reference = computed(() =>
  props.current && props.current.id !== props.item.id ? props.current.currentStats : null,
);

const isHull = computed(() => props.item.kind === 'HULL');
const isTurret = computed(() => props.item.kind === 'TURRET');

/**
 * Which cards have a bigger version worth opening.
 *
 * Hulls and turrets do: they have a mesh to turn and a stat block to compare, and
 * the card shows one fixed bearing of the first and one number per row of the
 * second. Liveries and titles do not — a livery's card *is* its pattern at full
 * size and a title's *is* the badge, rendered through the same component the
 * nameplate uses, so a window over either would be the same picture with more
 * padding round it.
 */
const openable = computed(() => isHull.value || isTurret.value);

/**
 * The same two kinds again, under the name the *layout* cares about.
 *
 * It is `openable`'s predicate to the letter today, and it is deliberately not
 * `openable`. That flag answers "is there a bigger version worth opening"; this
 * one answers "does this card carry a mesh still and a six-row stat block", and
 * the landscape rules at the end of this file rearrange the card around exactly
 * those two things — the picture into a fixed left column, the stats into a
 * multi-column block. A livery card's picture *is* its product at full width and
 * a title card's is a badge over a mock nameplate, and neither survives being
 * cropped into a 9.5 rem gutter.
 *
 * So the day a cosmetic becomes openable — a plausible product decision — it
 * must not silently inherit a layout built for a chassis. Two names for one
 * predicate is the cheap half of preventing that; the expensive half is finding
 * out from a screenshot.
 */
const mech = computed(() => isHull.value || isTurret.value);

/**
 * Which two meshes the still is composed from.
 *
 * Only the card's own item is drawn — see `showOnly` in `model-thumbnail.ts`.
 * The other slot still names the player's fitting, because `TankVisual` wants a
 * complete pair to build, but that half is hidden before the picture is taken.
 */
const previewHull = computed(() => (isHull.value ? props.item.id : props.counterpartId));
const previewTurret = computed(() => (isTurret.value ? props.item.id : props.counterpartId));

/**
 * What a livery actually includes, spelled out on the card.
 *
 * A cosmetic has no stat bar, and the space a stat bar would occupy is the
 * right place to answer the question a buyer is actually asking: *what do I
 * get*. Read from `shared/liveries.ts` rather than written per row, so a livery
 * whose pattern or finish changes cannot end up advertising the old one.
 */
const livery = computed(() => liveryFor(props.item.id));

/** The pattern file 404'd; the card degrades to the flat base-tint swatch. */
const patternFailed = ref(false);

/*
 * There used to be a caveat here, and its removal is the point of the change
 * that took it out.
 *
 * A livery shipped a track belt as well as a hull pattern, and the Drover and the
 * Colossus ride on tyres their artist drew (`HullProfile.wheeled`) — so on those
 * two, half of what a paint shipped was never going to appear, and the card had
 * to say so before the buy rather than let the player find out after it. A livery
 * reaches the hull and turret and nothing else now, so it is the same purchase on
 * every hull in the game and there is nothing left to warn about. Deleting a
 * warning is only ever right when the thing it warned about is gone.
 */

/**
 * Parts are stored per hull, and a hull the player does not own has none.
 *
 * So the button is drawn on every hull card — a control that appears and
 * disappears down a grid is harder to read than one that is consistently there —
 * but it is inert until the hull is bought. Opening the window anyway would
 * show the same thirty-two catalogue parts as every other hull, none of them
 * fitted, above a Fit button the server would refuse: `equipPart` checks
 * ownership of the hull as well as of the part. That is a dead end dressed up
 * as a feature. The server refuses the request too — this is the courtesy.
 */
const canCustomise = computed(() => isHull.value && props.item.owned);

/**
 * A paint job is bought every time it is worn, so its card never says *Owned*.
 *
 * The server reports `owned: false` for every paid paint whatever the database
 * holds — see `isConsumable` in `garage.service.ts` — so this card already falls
 * down the Buy branch by itself. What it needs on top is the *word*: a player
 * looking at the paint currently on their tank has to see that pressing this
 * charges them again, which "Buy" alone does not say once a Fitted badge is
 * sitting in the corner of the same card.
 */
const isConsumablePaint = computed(() => isPaint.value && props.item.id !== STOCK_PAINT_ID);

/**
 * The gate line, which is now a way out of the gate rather than a statement of
 * it. *Requires Light Frames* is the most useful sentence on a locked card —
 * it is the exact question "where do I go to fix this" — and it used to be a
 * paragraph you could read and not press.
 *
 * The two halves of it arrive independently from the server, so both halves of
 * the control degrade on their own:
 *
 *   **The id is what makes it pressable.** Without one there is nothing to
 *   focus, so the name stays the plain emphasis it always was rather than a
 *   button that navigates nowhere — a dead control is worse than a label,
 *   because a label does not promise anything.
 *
 *   **The name is only the label.** An id with no name is still a place worth
 *   going, so the button keeps working and says so in general terms. What it
 *   must never do is render an empty button, which is what
 *   `{{ item.requiredResearchName }}` alone would give.
 */
const gateNodeId = computed(() => props.item.requiredResearchId);
const gateLabel = computed(() => props.item.requiredResearchName ?? 'further research');
</script>

<template>
  <article
    class="card"
    :class="{ owned: item.owned, equipped: item.equipped, locked, openable, mech }"
    :data-item="item.id"
    @click="onCardClick"
  >
    <header>
      <div class="title">
        <h3>
          <button
            v-if="openable"
            type="button"
            class="open"
            :data-action="`inspect-${item.id}`"
            :aria-label="`Inspect ${item.name}`"
            @click="emit('open', item.id)"
          >
            {{ item.name }}
          </button>
          <template v-else>{{ item.name }}</template>
        </h3>
        <span class="tier">Tier {{ item.tier }}</span>
      </div>
      <span v-if="item.equipped" class="badge fitted">Fitted</span>
      <span v-else-if="item.owned && !item.retired" class="badge">Owned</span>
      <span v-else-if="item.owned" class="badge">Owned · not sold</span>
      <span
        v-else
        class="price"
        :class="{ short: !item.affordable && forSale, gem: isPremium && forSale, unsold: !forSale }"
      >
        <template v-if="priceAmount">
          {{ priceAmount }} <CurrencyIcon :kind="priceKind" />
        </template>
        <template v-else>{{ priceWord }}</template>
      </span>
    </header>

    <div v-if="isTitle" class="title-preview">
      <span
        v-if="spedUp"
        class="preview-speed"
        :title="`Preview at ${TITLE_PREVIEW_SPEED}× speed — in battle it takes the whole round`"
        data-test="title-preview-speed"
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M2 3.5v9l6-4.5zM8 3.5v9l6-4.5z" fill="currentColor" />
        </svg>
        ×{{ TITLE_PREVIEW_SPEED }}
      </span>
      <TitleBadge :title-id="item.id" variant="card" :speed="spedUp ? TITLE_PREVIEW_SPEED : 1" />
      <div class="title-plate" aria-label="As it appears over a tank">
        <TitleBadge :title-id="item.id" variant="plate" :speed="spedUp ? TITLE_PREVIEW_SPEED : 1" />
        <span class="title-plate-name">{{ nickname || 'Your name' }}</span>
        <span class="title-plate-bar"><i></i></span>
      </div>
    </div>

    <div v-else-if="isPaint" class="pattern" role="img" :aria-label="item.name">
      <img
        v-if="livery.pattern && !patternFailed"
        :src="livery.pattern"
        alt=""
        loading="lazy"
        @error="patternFailed = true"
      />
      <div v-else class="pattern-plain" :style="{ background: item.colorPrimary ?? livery.baseTint }"></div>
    </div>

    <ItemPreview
      v-else
      :hull-id="previewHull"
      :turret-id="previewTurret"
      :framing="isHull ? 'hull' : 'turret'"
      :label="item.name"
    />

    <p class="blurb">{{ item.blurb }}</p>

    <div v-if="isPaint" class="swatch">
      <i :style="{ background: item.colorPrimary ?? '#000' }"></i>
      <i :style="{ background: item.colorSecondary ?? '#000' }"></i>
    </div>

    <ul v-if="isPaint" class="includes">
      <li>{{ livery.pattern ? 'Projected pattern, hull and turret' : 'Factory finish' }}</li>
      <li v-if="livery.metallic !== null || livery.roughness !== null">Altered finish</li>
    </ul>

    <StatList
      v-if="!isPaint && !isTitle"
      :descriptors="descriptors"
      :values="item.currentStats"
      :against="reference"
    />

    <p v-if="researchLocked" class="gate">
      Requires
      <button
        v-if="gateNodeId"
        type="button"
        class="gate-link"
        :data-action="`gate-${item.id}`"
        @click="emit('showResearch', gateNodeId)"
      >
        {{ gateLabel }}
      </button>
      <strong v-else>{{ gateLabel }}</strong>
    </p>

    <p v-if="rankLocked" class="gate" :data-action="`rank-gate-${item.id}`">
      Issued at rank {{ item.requiredRank }} — <strong>{{ item.requiredRankTitle }}</strong>
    </p>

    <p
      v-if="awardedAt !== null && !item.owned"
      class="gate award"
      :data-action="`award-${item.id}`"
    >
      Awarded at rank {{ awardedAt }} — <strong>{{ item.awardedAtRankTitle }}</strong>.
      Not for sale.
    </p>

    <p v-else-if="item.retired && item.owned" class="gate" :data-action="`retired-${item.id}`">
      Withdrawn from the shop. Yours to keep, and it still fits.
    </p>

    <div v-if="item.owned && item.maxUpgradeLevel > 0" class="levels">
      <span class="levels-label">Upgrade</span>
      <span class="pips">
        <i
          v-for="n in item.maxUpgradeLevel"
          :key="n"
          :class="{ filled: n <= item.level }"
        ></i>
      </span>
      <span class="levels-count">{{ item.level }} / {{ item.maxUpgradeLevel }}</span>
    </div>

    <div v-if="showsPrestige" class="levels top-tier" :class="{ locked: !item.prestigeUnlocked }">
      <span class="levels-label">Top tier</span>
      <span class="pips">
        <i v-for="n in item.maxPrestigeLevel" :key="n" :class="{ filled: n <= item.prestige }"></i>
      </span>
      <span class="levels-count">{{ item.prestige }} / {{ item.maxPrestigeLevel }}</span>
    </div>

    <footer>
      <button
        v-if="!item.owned && forSale"
        type="button"
        class="primary"
        :class="{ gem: isPremium }"
        :data-action="`buy-${item.id}`"
        :disabled="!canBuy || busy"
        @click="emit('purchase', item.id)"
      >
        {{
          locked
            ? 'Locked'
            : !item.affordable
              ? isPremium
                ? 'Not enough diamonds'
                : 'Not enough credits'
              : isConsumablePaint
                ? item.equipped
                  ? 'Repaint'
                  : 'Paint it'
                : 'Buy'
        }}
      </button>

      <template v-else-if="item.owned">
        <button
          type="button"
          class="primary"
          :data-action="`equip-${item.id}`"
          :disabled="item.equipped || busy"
          @click="emit('equip', item.id)"
        >
          {{
            item.equipped
              ? isTitle
                ? 'Worn'
                : 'Fitted'
              : isPaint
                ? 'Apply'
                : isTitle
                  ? 'Wear'
                  : 'Fit'
          }}
        </button>

        <UpgradeInstallButton
          v-if="item.upgrade"
          :item-id="item.id"
          :install="item.upgrade"
          :diamonds="diamonds"
          :busy="busy"
          @install="(id) => emit('installUpgrade', id)"
          @skip="(id) => emit('skipUpgrade', id)"
        />

        <button
          v-else-if="item.maxUpgradeLevel > 0 && !maxed"
          type="button"
          :data-action="`upgrade-${item.id}`"
          :disabled="!canUpgrade || busy"
          @click="emit('upgrade', item.id)"
        >
          Upgrade · {{ formatCredits(item.nextUpgradeCost ?? 0) }}
          <CurrencyIcon kind="credits" />
        </button>

        <button
          v-else-if="showsPrestige"
          type="button"
          class="gem"
          :data-action="`prestige-${item.id}`"
          :disabled="!canPrestige || busy"
          @click="emit('prestige', item.id)"
        >
          <template v-if="prestigeMaxed">Top tier reached</template>
          <template v-else-if="!item.prestigeUnlocked">Fully upgraded</template>
          <template v-else>
            Top tier · {{ item.nextPrestigeCost }} <CurrencyIcon kind="diamonds" />
          </template>
        </button>
      </template>
    </footer>

    <button
      v-if="isHull"
      type="button"
      class="customise"
      :data-action="`customise-${item.id}`"
      :disabled="!canCustomise || busy"
      :title="canCustomise ? undefined : 'Buy this hull to fit parts to it'"
      @click="emit('customise', item.id)"
    >
      Customize
    </button>
  </article>
</template>

<style scoped>
/*
 * The whole card is pressable, so it says so — but only on the kinds that open
 * something. A cosmetic card keeps the default cursor, because nothing happens
 * when it is clicked and a pointer that promises otherwise is worse than none.
 */
.card.openable {
  cursor: pointer;
}

/* The heading's control is the heading: it inherits the type it replaced, so the
   card looks identical and the focus ring is the only thing added. */
.open {
  padding: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  background: none;
  border: 0;
  cursor: pointer;
}

.open:hover {
  color: var(--accent, #e8722c);
}

.card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--steel-700);
  border-radius: 4px;
  background: var(--steel-800);
}

.card.equipped {
  border-color: var(--accent);
  box-shadow: inset 0 0 0 1px rgba(232, 114, 44, 0.25);
}

.card.locked {
  opacity: 0.72;
}

header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

/*
 * Scoped to `header`, and that is not tidiness.
 *
 * A Vue `scoped` stylesheet stamps its attribute onto a **child component's root
 * node** too, and `TitleBadge`'s root used to carry a class called `title` — so
 * this rule reached inside it and stood the badge on end on every card in the
 * shop. The badge's classes are prefixed now, which is the real fix; narrowing
 * this is the second half, so the next component that ships a `.title` does not
 * find the same trap laid for it.
 */
header .title {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

h3 {
  margin: 0;
  font-size: 1rem;
  letter-spacing: 0.05em;
  color: #fff;
}

.tier {
  font-size: 0.65rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--steel-400);
}

.badge,
.price {
  flex: none;
  padding: 0.2rem 0.45rem;
  border-radius: 2px;
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
}

.badge {
  border: 1px solid var(--steel-600);
  color: var(--steel-200);
}

.badge.fitted {
  border-color: var(--accent);
  color: var(--accent);
}

.price {
  font-family: ui-monospace, 'SF Mono', monospace;
  color: #fff;
  background: var(--steel-700);
}

.price.short {
  color: #d9705f;
}

/* The diamond blue the top-tier control already uses, so "this costs the other
   currency" reads the same wherever it appears. `.short` still wins on colour,
   because "you cannot afford it" is the more urgent of the two facts. */
.price.gem {
  color: #9fd7f5;
  background: rgba(79, 143, 189, 0.18);
}

.price.gem.short {
  color: #d9705f;
}

/*
 * Not a price at all, so it must not look like one.
 *
 * `No longer sold` and `Awarded` sit in the slot a figure sits in, and the whole
 * job of this rule is to stop them being read as a *number* at a glance: no
 * monospace, no white, no filled chip — the muted, unemphasised treatment of a
 * label. A cost the player cannot pay is drawn in red; a cost that does not exist
 * should be drawn quietly.
 */
.price.unsold {
  font-family: inherit;
  color: var(--steel-400);
  background: none;
  border: 1px solid var(--steel-700);
}

.blurb {
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: var(--steel-400);
}

/*
 * The livery's cloth. Same box the mesh previews hold (8/5, bordered), so a
 * paint card and a hull card keep one silhouette down the grid; inside it is
 * the pattern file itself, cover-cropped — a material swatch, not a scene —
 * with the belt texture as a strip along the bottom edge where a track
 * belongs. A missing or absent pattern is a flat base-tint field, which is
 * honestly what that livery is.
 */
.pattern {
  position: relative;
  aspect-ratio: 8 / 5;
  border-radius: 3px;
  overflow: hidden;
  border: 1px solid var(--steel-700);
  background: var(--steel-900);
}

.pattern img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pattern-plain {
  position: absolute;
  inset: 0;
}

.swatch {
  display: flex;
  /* Slimmed from 46px now that it sits under a render of the actual tank
     rather than standing in for one. It is a colour label, not the picture. */
  height: 10px;
  border-radius: 3px;
  overflow: hidden;
  border: 1px solid var(--steel-700);
}

/* What the livery ships with, where a stat bar would be on a hull card. */
.includes {
  margin: 0;
  padding: 0 0 0 1rem;
  list-style: none;
  display: grid;
  gap: 0.18rem;
  font-size: 0.72rem;
  color: var(--steel-400);
}

.includes li {
  position: relative;
}

.includes li::before {
  content: '';
  position: absolute;
  left: -0.8rem;
  top: 0.42em;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--accent-dim);
}

.swatch i {
  flex: 1;
}

.gate {
  margin: 0;
  padding: 0.45rem 0.6rem;
  border-left: 2px solid var(--accent-dim);
  background: rgba(232, 114, 44, 0.08);
  font-size: 0.75rem;
}

.gate strong {
  color: var(--accent);
}

/*
 * An award is good news and a gate is bad news, so they cannot share a colour.
 *
 * The orange bar on `.gate` is the screen's "you cannot have this yet" cue and
 * it is used everywhere for exactly that. A rung that is *giving* something away
 * takes the diamond blue the shop already uses for the premium shelf — the one
 * accent in the palette that means "this is the good one" rather than "this is
 * blocked".
 */
.gate.award {
  border-left-color: #4f8fbd;
  background: rgba(79, 143, 189, 0.1);
}

.gate.award strong {
  color: #9fd7f5;
}

/*
 * A button that reads as a link, sitting inside a sentence.
 *
 * Everything a `<button>` brings by default — the box, the padding, the middle
 * baseline — would break the line it lives in, so all of it is stripped and the
 * one thing that says *press me* is put back: the accent colour the node name
 * already had, plus an underline. The underline is what stops it reading as
 * emphasis, which is exactly what this used to be and what a coloured word with
 * no other cue still looks like.
 *
 * `text-align: left` and the inline display matter for the wrap: a long node
 * name has to break like the rest of the sentence rather than pushing the line
 * wider than the card.
 */
.gate-link {
  display: inline;
  padding: 0;
  border: 0;
  background: none;
  color: var(--accent);
  font: inherit;
  font-weight: 600;
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 0.15em;
  cursor: pointer;
}

.gate-link:hover {
  color: #fff;
  text-decoration-thickness: 2px;
}

/* Keyboard users get the ring the pointer never needed. */
.gate-link:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: 2px;
}

.levels {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.68rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--steel-400);
}

.levels-label {
  flex: none;
}

.pips {
  display: flex;
  flex: 1;
  gap: 3px;
}

.pips i {
  flex: 1;
  height: 6px;
  border-radius: 1px;
  background: var(--steel-700);
}

.pips i.filled {
  background: var(--accent);
}

.top-tier .pips i.filled {
  background: #9fd7f5;
}

.top-tier.locked {
  opacity: 0.5;
}

.levels-count {
  font-family: ui-monospace, 'SF Mono', monospace;
}

footer {
  margin-top: auto;
  display: flex;
  gap: 0.4rem;
}

/* Secondary to the buy/fit row above it: customising is what you do *after*
   owning the hull, so it should not compete with the control that gets you
   there. */
.card > .customise {
  padding: 0.5rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: none;
  color: var(--steel-200);
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.card > .customise:not(:disabled):hover {
  border-color: var(--accent);
  color: var(--accent);
}

.card > .customise:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

footer button {
  flex: 1;
  padding: 0.55rem 0.5rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: var(--steel-700);
  color: var(--steel-200);
  font-size: 0.75rem;
  letter-spacing: 0.04em;
}

footer button.primary {
  border-color: transparent;
  background: var(--accent);
  color: #12161c;
  font-weight: 600;
}

footer button.gem {
  border-color: #3f6e88;
  background: rgba(79, 143, 189, 0.18);
  color: #9fd7f5;
}

/* A premium Buy is still the card's primary control — it is the one thing the
   card is for — so it keeps the accent's weight and takes the diamond colour
   rather than the quieter top-tier treatment. */
footer button.primary.gem {
  border-color: #3f6e88;
  background: #9fd7f5;
  color: #12161c;
}

footer button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/*
 * Small desktop: the still trims a little shorter, once three of these share a
 * row instead of two.
 *
 * `ItemPreview`'s own box is a fixed `aspect-ratio: 8 / 5`, so at three
 * narrower columns (see `.grid-mech`'s own small-desktop rule in
 * `GarageScreen.vue`) the picture stays exactly as tall relative to its own
 * width — three columns of the same photograph, just smaller. Shortening the
 * ratio a little here is what actually shrinks it, giving the blurb and the
 * stat block underneath a bit more of the card's own height back rather than
 * ceding all of it to a picture three cards are now sharing the row for.
 *
 * A range rather than an open `max-width`, because it stops mattering below
 * `.grid-mech`'s own two-column tier: two cards share the row there and each
 * one is wide enough again that the ordinary 8/5 box is the right shape —
 * trimming it too would be solving a height problem the two-column tier does
 * not have.
 *
 * `.garage:not(.compact)` because this card renders inside `.grid-mech` on a
 * landscape phone too, where the preview is a fixed-width picture in a gutter
 * whose height the compact rules below measure to the pixel — a width query
 * reaching into a layout it was not measured against is the exact fault
 * chased out of this session's other small-desktop rules.
 */
@media (min-width: 68.76rem) and (max-width: 100rem) {
  .garage:not(.compact) .grid-mech .card.mech :deep(.preview) {
    aspect-ratio: 8 / 4.4;
  }
}

/* ─── The title card's two previews ──────────────────────────────────────── */

.title-preview {
  position: relative;
  display: grid;
  gap: 0.6rem;
  justify-items: center;
  padding: 1rem 0.8rem 0.8rem;
  border: 1px solid var(--steel-700);
  border-radius: 3px;
  /*
   * A dark, slightly uneven ground rather than the card's flat panel. Every
   * colour on this shelf was chosen to read over grass, buildings and smoke, so
   * showing it on a lighter surface than the arena's would flatter it.
   */
  background:
    radial-gradient(120% 80% at 50% 0%, rgba(255, 255, 255, 0.05), transparent 70%),
    #14181e;
}

/*
 * The honest half: the badge at the size a nameplate actually draws it, beside
 * a name and a health bar, over the same dark ground. Nothing here is scaled —
 * `variant="plate"` is the arena's own size, so what is on this strip is what
 * an enemy sixty metres away will see.
 */
.title-plate {
  display: grid;
  justify-items: center;
  gap: 2px;
  padding: 0.35rem 0.6rem 0.45rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  width: 100%;
}

.title-plate-name {
  font-size: 0.62rem;
  color: var(--steel-200);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
}

.title-plate-bar {
  display: block;
  width: 56px;
  height: 5px;
  border: 1px solid rgba(0, 0, 0, 0.7);
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.55);
  overflow: hidden;
}

.title-plate-bar i {
  display: block;
  width: 72%;
  height: 100%;
  background: #4caf6d;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * A PHONE HELD SIDEWAYS — the card turns with it
 *
 * Gated on `.garage.compact`, which is `GarageScreen`'s single `matchMedia`
 * answer to `(max-height: 520px)` and not a second opinion about it. Desktop and
 * tablet never see a declaration in this block.
 *
 * ── What was wrong ────────────────────────────────────────────────────────
 *
 * Measured at 667×375 with `e2e/cases/garage-landscape.mjs`, after the sidebar
 * was dropped and the first card was brought up to y=75:
 *
 *   the content area is **300 px tall**
 *   `.grid` was 627×7329 — two columns of 313 px, eleven rows
 *   so a card was **666 px tall**, and *less than half of one* was on screen
 *
 * A shelf you can only read by scrolling a card at a time is not a shelf. And
 * the fault is not the card's height in isolation — it is that a *portrait* card
 * (picture on top, everything stacked under it) is the wrong shape for a
 * viewport that is 667 wide and 300 tall. The card was fighting the orientation.
 *
 * ── So the card turns ninety degrees ──────────────────────────────────────
 *
 * One column, and the card becomes a horizontal band: the picture in a fixed
 * left gutter, and the name, the price, the stat block and the controls in the
 * column beside it. Landscape means width is what there is a lot of, so width is
 * what the layout spends. Two things follow that are worth stating, because both
 * read as losses until the arithmetic is done:
 *
 *   **One column of wide cards beats two columns of narrow ones here.** Two
 *   313 px columns can show two cards at once only if each fits in 300 px, and
 *   nothing that holds a picture *and* six compared stats fits in 300 px at
 *   313 px wide — something has to go, and the only candidate large enough is
 *   the stat block. That is exactly the thing the sidebar was dropped to make
 *   room for, so cutting it here would undo the previous step to pay for this
 *   one. One 627 px card carries all of it and still comes out shorter.
 *
 *   **The stat block is the reason these two tabs exist on a phone.** Six rows
 *   in one column is 113 px; six rows in three columns, each cell stacking its
 *   label over its figure, is 68 px for the same six numbers and the same six
 *   comparison tracks. Nothing is dropped — the block is folded.
 *
 * ── What is dropped, and by what rule ─────────────────────────────────────
 *
 * The blurb, and only the blurb. The required reading on this card is the name,
 * the tier, the picture, the price, the delta against what is fitted, and the
 * control — the blurb is the one element on it that is flavour rather than a
 * fact a purchase turns on, and it is two lines of the tightest budget on the
 * screen. It is not lost: a tap anywhere on the card opens the workbench, which
 * carries the blurb in full at inspection size. The same argument the sidebar
 * was dropped on, one element down.
 * ═══════════════════════════════════════════════════════════════════════════ */

.garage.compact .card.mech {
  /*
   * The gutter is stated once and read three times — the padding that reserves
   * it, the picture that sits in it and the Customize button under the picture.
   * Written as three literals it is three chances for the button to hang half a
   * pixel off the picture's edge.
   */
  --gutter: 9.5rem;
  --pad-x: 0.65rem;

  position: relative;
  gap: 0.45rem;
  padding: 0.55rem var(--pad-x) 0.65rem;
  padding-left: calc(var(--gutter) + var(--pad-x) + 0.6rem);
  /*
   * Tall enough that the picture and the Customize button below it cannot meet,
   * since both are out of flow and neither can push the other. Every real card
   * is taller than this — it is a floor, not a height.
   */
  min-height: 11rem;
}

/*
 * The picture leaves the flow rather than becoming a grid or flex column.
 *
 * A two-column *grid* was the obvious shape and does not survive this card's
 * children: they are a variable set — up to three gate lines, two ladder rows,
 * a footer and a Customize button, each independently optional — so the picture
 * would have to span a row count nobody can name. `grid-row: 1 / -1` resolves
 * against the explicit grid and silently spans one; `span 99` conjures
 * ninety-eight empty rows and pays the row gap on every one of them. Taking the
 * picture out of flow and reserving its width with padding needs no row
 * arithmetic at all, and the column beside it is the same ordinary flex column
 * it is on a desktop, so a gate line that appears on a locked card lands where
 * it always did.
 *
 * `ItemPreview`'s root carries its own `aspect-ratio: 8 / 5`, so one width is
 * the whole size: 152 px of picture is 95 px tall.
 */
.garage.compact .card.mech .preview {
  position: absolute;
  left: var(--pad-x);
  top: 0.55rem;
  width: var(--gutter);
}

/* One line: the name, its tier beside it, the price at the far end. Stacked, the
   tier cost a row of the eight this card gets. */
.garage.compact .card.mech header {
  align-items: center;
  gap: 0.5rem;
}

.garage.compact .card.mech header .title {
  flex-direction: row;
  align-items: baseline;
  gap: 0.45rem;
  min-width: 0;
}

.garage.compact .card.mech h3 {
  min-width: 0;
  font-size: 0.92rem;
}

/*
 * The name is the card's keyboard route into the workbench, and it measured
 * **49×19** — the smallest tap target on the tab, on a phone, against the 44 px
 * floor `CLAUDE.md` sets project-wide. It is given the height rather than
 * padding, so the text stays on its baseline and the box grows around it, and
 * the header row absorbs the cost: at 44 px the row is the same height it
 * already was with a name and a tier stacked in it. Free, in other words.
 */
.garage.compact .card.mech .open {
  display: flex;
  align-items: center;
  min-height: 44px;
  /*
   * And 44 px *wide*, which the height alone did not buy: this button is as wide
   * as its own label, so `Dart` measured 42×44 where `Marauder` passed. A floor
   * on one axis is not a tap target. The text stays left-aligned, so on every
   * name longer than four characters this declaration does nothing at all.
   */
  min-width: 44px;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Flavour, and the workbench has it in full. See the block comment above. */
.garage.compact .card.mech .blurb {
  display: none;
}

/*
 * Six stats folded into three columns of two rather than one column of six.
 *
 * Only `grid-template-columns` moves on the list itself — the rows are already
 * grid items, so they flow into the new columns with nothing else said. Inside a
 * row the label stops sharing a line with the figure: at 149 px a cell cannot
 * hold `MUZZLE VELOCITY` and `640 m/s` and a `+120` delta side by side, and the
 * stacked cell is 31 px against the 33 px the side-by-side row cost anyway. The
 * comparison track keeps spanning the cell, so the ghost that marks the fitted
 * part is still under every figure — that ghost *is* the "against what is
 * fitted", and it is the cheapest information on the card at 4 px.
 */
.garage.compact .card.mech :deep(.stats) {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.4rem 0.7rem;
}

/* Three across once there is room for three. 640 px is where a 152 px gutter
   still leaves three 136 px cells; below it two columns and three rows, which is
   14 px taller and legible, rather than three columns that clip a label. */
@media (min-width: 40rem) {
  .garage.compact .card.mech :deep(.stats) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.garage.compact .card.mech :deep(.stats .row) {
  grid-template-columns: minmax(0, 1fr);
  gap: 0.1rem;
}

.garage.compact .card.mech :deep(.stats dt) {
  font-size: 0.58rem;
  letter-spacing: 0.06em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.garage.compact .card.mech :deep(.stats dd) {
  font-size: 0.72rem;
  gap: 0.3rem;
}

.garage.compact .card.mech :deep(.stats .delta) {
  font-size: 0.64rem;
}

/* One line each, and the ladders lose their padding rather than their pips: the
   count is the fact and the pips are the picture of it, and at 5 px the picture
   is still readable. */
.garage.compact .card.mech .levels {
  gap: 0.4rem;
  font-size: 0.6rem;
  letter-spacing: 0.06em;
}

.garage.compact .card.mech .pips i {
  height: 5px;
}

/*
 * The gate line is a sentence with a button in it, and the button measured
 * **60×15**. It cannot simply be given 44 px of height as a block without
 * breaking the sentence around it, so it is given 44 px as an `inline-flex` and
 * the paragraph grows to hold it — the line still reads as a line, and the
 * pressable area is the whole height of it. This is the one control on the card
 * whose target had to be bought with a row rather than absorbed by one.
 */
.garage.compact .card.mech .gate {
  padding: 0.15rem 0.5rem;
  font-size: 0.7rem;
  line-height: 1.3;
}

.garage.compact .card.mech .gate-link {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
}

/* Buy, Fit, Upgrade, Top tier — all of them measured 35 px tall. */
.garage.compact .card.mech footer button {
  min-height: 44px;
  padding: 0.35rem 0.4rem;
  font-size: 0.72rem;
}

/*
 * The same floor for the cards that do NOT turn horizontal — the Cosmetics
 * shelf's liveries and titles, which keep their portrait shape because a livery
 * card *is* its pattern at full size. Measured at 667×375 before this rule:
 * Apply/Wear at 413×35 and the gate link at 60×15, on the tab whose whole
 * subject is things bought by tapping. Stated on the bare `.card` so a future
 * third shelf inherits it, and harmless on `.mech`, whose own rules above say
 * the same thing.
 */
.garage.compact .card footer button {
  min-height: 44px;
}

.garage.compact .card .gate-link {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
}

/*
 * Customize goes into the dead space under the picture, and that is what pays
 * for the row it used to own.
 *
 * The gutter is 152 px wide and its picture is 95 px tall, so on a card of any
 * real height there is an empty left column below it doing nothing. A 44 px
 * button is exactly what belongs there: it is the hull's own control, it sits
 * directly under the hull's own picture, and it costs the text column no row at
 * all — 51 px of the tightest budget on the screen, recovered by moving one
 * element rather than by cutting anything.
 *
 * Pinned to the *bottom* rather than measured down from the picture, so it lands
 * on the card's own edge whatever the text column's height turns out to be, and
 * `min-height` on the card is what guarantees it cannot reach the picture.
 */
.garage.compact .card.mech > .customise {
  position: absolute;
  left: var(--pad-x);
  bottom: 0.65rem;
  width: var(--gutter);
  min-height: 44px;
  padding: 0.35rem;
  font-size: 0.66rem;
}


/* The preview's pace, top right of the preview ground — a label, not a control. */
.preview-speed {
  position: absolute;
  top: 0.3rem;
  right: 0.35rem;
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.1rem 0.3rem;
  border: 1px solid var(--steel-700);
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.45);
  color: var(--steel-200);
  font-size: 0.62rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
}

.preview-speed svg {
  width: 0.7rem;
  height: 0.7rem;
}
</style>
